import { onBeforeUnmount, reactive } from "vue";
import { isComponentScope } from "../component";
import { onHotReload } from "../debug/onHotreload";

type Listener<K extends keyof WindowEventMap> = (this: Window, ev: WindowEventMap[K]) => any;

export type WindowEventListenerDestructor = () => void;


export type UseWindowEvents = {
  addEventListener: <K extends keyof WindowEventMap>(name: K, listener: Listener<K>) => WindowEventListenerDestructor;
  destroyAll: () => void;
}

export const useWindowEvents = (): UseWindowEvents => {
  const state = reactive<{
    listeners: Record<string, Listener<any>[]>
    destructors: WindowEventListenerDestructor[]
  }>({
    listeners: {},
    destructors: []
  })
  const addEventListener = <K extends keyof WindowEventMap>(name: K, listener: Listener<K>) => {
    const slot = state.listeners[name] || [];
    if (slot.includes(listener)) return () => {};
    slot.push(listener);
    state.listeners[name] = slot;

    window.addEventListener(name, listener);

    const destroy = () => {
      window.removeEventListener(name, listener);
      state.listeners[name] = (state.listeners[name] || []).filter(it => it !== listener);
    }

    state.destructors = [...state.destructors, destroy];

    return destroy;
  }

  const destroyAll = () => {
    state.destructors.forEach(it => it());
    state.destructors = [];
  }

  if (isComponentScope()) {
    onBeforeUnmount(() => {
      destroyAll();
    })
  }

  onHotReload(() => {
    const listeners = Object.entries(state.listeners);
    destroyAll();

    listeners.forEach(([key, value]) => {
      value.forEach((fun) => {
        addEventListener(key as keyof WindowEventMap, fun);
      })
    })
  })

  return { addEventListener, destroyAll };
}
